import { useMutation, useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssessmentType,
  EngagementSurveyReportGroupBy,
  EngagementSurveyRollupType,
  ExportEngagementSurveyToCsvMutationMutation,
  ExportEngagementSurveyToCsvMutationMutationVariables,
  GetComplianceProgramEngagementSurveyReportQuery,
  GetComplianceProgramEngagementSurveyReportQueryVariables,
} from "types/graphql-schema";

import EngagementSurveyDeliveryAllQuestions from "@apps/engagement-survey/components/delivery/engagement-survey-delivery-all-questions";
import useSurveyDeliveryData from "@apps/engagement-survey/components/delivery/use-survey-delivery-data";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import exportEngagementSurveyCsvMutation from "../graphql/export-engagement-survey-csv-mutation";
import getComplianceProgramEngagementSurveyReportQuery from "../graphql/get-compliance-program-engagement-survey-report-query";
import EngagementSurveyReportTable from "./engagement-survey-report/engagement-survey-report-table";
import EngagementSurveyReportViewOptionsSidebar from "./engagement-survey-report/engagement-survey-view-options-sidebar";
import { initEngagementSurveyReportViewOptions } from "./engagement-survey-report/helpers";
import { EngagementSurveyReportViewOptions } from "./engagement-survey-report/types";
import useSurveyReportFilters from "./engagement-survey-report/use-survey-report-filters";
import useReportingViews from "./reporting-views/use-reporting-views";

enum EngagementSurveyReportViewType {
  HEATMAP,
  RAW_DATA,
}

const defaultViewOptions = {
  ...initEngagementSurveyReportViewOptions,
  groupBy: EngagementSurveyReportGroupBy.Team,
};

const EngagementSurveyReport = ({
  complianceProgramId,
  headerContent,
  isInReporting,
}: {
  complianceProgramId: number;
  headerContent?: JSX.Element;
  isInReporting?: boolean;
}) => {
  const isAdmin = isAdminVar();
  const label = useLabel();
  const currentUser = currentUserVar();
  const [showViewOptionsSidebar, setShowViewOptionsSidebar] = useState(false);
  const [baselineViewOptions, setBaselineViewOptions] =
    useState<EngagementSurveyReportViewOptions>(defaultViewOptions);
  const [viewOptions, setViewOptions] =
    useState<EngagementSurveyReportViewOptions>(defaultViewOptions);
  const [viewType, setViewType] = useState(
    EngagementSurveyReportViewType.HEATMAP,
  );

  const handleUpdateViewOptions = useCallback(
    (newOptions: Partial<EngagementSurveyReportViewOptions>) => {
      setViewOptions((prev) => ({
        ...prev,
        isDirty: true,
        ...newOptions,
      }));
    },
    [],
  );

  const handleResetViewOptionsToBaseline = useCallback(() => {
    setViewOptions(baselineViewOptions);
  }, [baselineViewOptions]);

  const handleResetViewOptionsToEmpty = useCallback(() => {
    setBaselineViewOptions(defaultViewOptions);
    setViewOptions(defaultViewOptions);
  }, []);

  const {
    reportingViewsData,
    getReportingViews,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
  } = useReportingViews({
    complianceProgramId,
    assessmentType: AssessmentType.EngagementSurvey,
    viewOptions,
    baselineViewOptions,
    onResetViewOptionsToBaseline: handleResetViewOptionsToBaseline,
    onUpdateViewOptions: handleUpdateViewOptions,
    onUpdateBaselineViewOptions: setBaselineViewOptions,
    onResetViewOptionsToEmpty: handleResetViewOptionsToEmpty,
  });

  useEffect(() => {
    // reset everything if program changes
    setBaselineViewOptions(defaultViewOptions);
    setViewOptions(defaultViewOptions);
    // Load saved views for this program
    getReportingViews({
      variables: {
        complianceProgramId,
        assessmentType: AssessmentType.EngagementSurvey,
      },
    });
  }, [complianceProgramId, getReportingViews]);

  const graphqlFilters = useSurveyReportFilters({ viewOptions });

  const {
    data: programData,
    loading: isLoadingProgram,
    previousData: previousProgramData,
  } = useQuery<
    GetComplianceProgramEngagementSurveyReportQuery,
    GetComplianceProgramEngagementSurveyReportQueryVariables
  >(getComplianceProgramEngagementSurveyReportQuery, {
    variables: {
      complianceProgramId,
      groupBy: viewOptions.groupBy ?? EngagementSurveyReportGroupBy.Team,
      teamIds: graphqlFilters.teamIdsFilter,
      managerIds: graphqlFilters.managerIdsFilter,
      managerIdsFullTree: graphqlFilters.managerIdsFullTreeFilter,
      employeeStartDateRange: graphqlFilters.employeeStartDateRangeFilter,
      locations: graphqlFilters.locationsFilter,
      collapsedKeys: viewOptions.hiddenColumns,
    },
  });

  const complianceProgram = useMemo(
    () =>
      programData
        ? programData.complianceProgram
        : previousProgramData
          ? previousProgramData.complianceProgram
          : null,
    [programData, previousProgramData],
  );

  const assessmentDeliveryId = useMemo(
    () => complianceProgram?.assessmentDeliveries.edges[0]?.node?.id,
    [complianceProgram],
  );

  const { assessmentDeliveryData, isLoadingAssessmentDelivery } =
    useSurveyDeliveryData({
      assessmentDeliveryId,
      viewOptions,
      filters: graphqlFilters,
    });

  const [exportToCSV, { loading: isExporting }] = useMutation<
    ExportEngagementSurveyToCsvMutationMutation,
    ExportEngagementSurveyToCsvMutationMutationVariables
  >(exportEngagementSurveyCsvMutation);

  const exportProgram = useCallback(() => {
    if (!complianceProgram?.engagementSurveyReport || isExporting) {
      return;
    }
    exportToCSV({
      variables: {
        complianceProgramId,
        groupBy: viewOptions.groupBy ?? EngagementSurveyReportGroupBy.Team,
        teamIds: graphqlFilters.teamIdsFilter,
        managerIds: graphqlFilters.managerIdsFilter,
        managerIdsFullTree: graphqlFilters.managerIdsFullTreeFilter,
        employeeStartDateRange: graphqlFilters.employeeStartDateRangeFilter,
        locations: graphqlFilters.locationsFilter,
        collapsedKeys: viewOptions.hiddenColumns,
      },
    });
    successNotificationVar({
      title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
    });
  }, [
    complianceProgram,
    isExporting,
    exportToCSV,
    complianceProgramId,
    viewOptions,
    graphqlFilters,
    currentUser,
  ]);

  const responders = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.responders.edges
          .map((edge) => assertNonNull(edge?.node))
          .filter(
            ({ responderCount, totalResponders }) =>
              responderCount > 0 || totalResponders > 0,
          )
      : [];
  }, [complianceProgram]);

  const orgRollup = useMemo(() => {
    return responders.find(
      ({ rollupType }) =>
        rollupType === EngagementSurveyRollupType.Organization,
    );
  }, [responders]);

  const npsQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.npsQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node),
        )
      : [];
  }, [complianceProgram]);
  const rangeQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.rangeQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node),
        )
      : [];
  }, [complianceProgram]);

  const savedViewOptions = useMemo(() => {
    return reportingViewsData?.complianceProgramReportingViews
      ? assertEdgesNonNull(
          reportingViewsData.complianceProgramReportingViews,
        ).map((opt) => ({
          value: opt.id,
          label: opt.name ?? "",
        }))
      : [];
  }, [reportingViewsData]);

  const handleChangeSavedView = useCallback(
    (opt: ComboboxGenericOption<number>) => {
      if (!reportingViewsData?.complianceProgramReportingViews) {
        return;
      }
      const newViewOptionsData = assertEdgesNonNull(
        reportingViewsData.complianceProgramReportingViews,
      ).find((view) => view.id === opt.value);
      const newViewOptions = {
        id: newViewOptionsData?.id,
        name: newViewOptionsData?.name ?? "",
        isDirty: false,
        ...JSON.parse(newViewOptionsData?.viewOptions),
      };
      setViewOptions(newViewOptions);
      setBaselineViewOptions(newViewOptions);
    },
    [reportingViewsData],
  );

  const reportDropdownActions = useMemo(() => {
    const isWorking =
      isSavingReportingView ||
      isDeletingReportingView ||
      isExporting ||
      isLoadingProgram;
    return removeConsecutiveDividers(
      compact([
        viewOptions.id && {
          label: "Duplicate this view",
          onClick: handleDuplicateView,
          disabled: isWorking,
        },
        viewOptions.id &&
          viewOptions.isDirty && {
            label: "Save as new view",
            onClick: handleSaveAsNewView,
            disabled: isWorking,
          },
        viewOptions.id && {
          label: "Delete this view",
          onClick: handleDeleteView,
          disabled: isWorking,
        },
        {
          type: "divider",
        },
        isInReporting && {
          type: "link",
          label: "View Program Progress",
          to: `/programs/${complianceProgramId}/progress`,
          disabled: isWorking,
        },
        {
          type: "divider",
        },

        viewType === EngagementSurveyReportViewType.HEATMAP &&
          (orgRollup?.responderCount ?? 0) > 0 && {
            label: "Export to CSV",
            onClick: exportProgram,
            disabled: isWorking,
          },
      ]),
    );
  }, [
    viewOptions,
    handleDeleteView,
    handleDuplicateView,
    handleSaveAsNewView,
    isExporting,
    isSavingReportingView,
    isDeletingReportingView,
    isInReporting,
    viewType,
    complianceProgramId,
    isLoadingProgram,
    orgRollup,
    exportProgram,
  ]);

  if (
    !isLoadingProgram &&
    npsQuestions.length === 0 &&
    rangeQuestions.length === 0
  ) {
    return (
      <Layout.MainSubSectionEmpty>
        <div className="flex items-center justify-center gap-2">
          <TopicflowIcon type="info" className="h-4 w-4" />
          <div>
            There are no range or NPS questions in this survey. To view all the
            responses,{" "}
            <AppLink
              className="text-blue-600 hover:underline"
              to={`/survey/delivery/${complianceProgram?.assessmentDeliveries?.edges[0]?.node?.id}`}
            >
              view the full survey here
            </AppLink>
            .
          </div>
        </div>
      </Layout.MainSubSectionEmpty>
    );
  }

  return (
    <div>
      <ReportingViewDeletionConfirm />
      {showViewOptionsSidebar && (
        <EngagementSurveyReportViewOptionsSidebar
          viewOptions={viewOptions}
          responders={responders}
          isLoading={isLoadingProgram}
          onUpdateViewOptions={handleUpdateViewOptions}
          onClose={() => setShowViewOptionsSidebar(false)}
          onDeleteView={handleDeleteView}
          onDuplicateView={handleDuplicateView}
          onSaveView={handleSaveExistingView}
          onSaveAsNewView={handleSaveAsNewView}
          onResetViewToBaseline={handleResetViewOptionsToBaseline}
        />
      )}
      <div
        className={classNames(
          isInReporting && "px-8 pb-4 z-30 bg-white sticky top-0",
        )}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {headerContent}
            <ComboboxGeneric
              prefix="View"
              options={savedViewOptions}
              disabled={isSavingReportingView || isDeletingReportingView}
              onChangeValue={handleChangeSavedView}
              value={
                savedViewOptions.find(
                  ({ value }) => value === viewOptions.id,
                ) ?? null
              }
              placeholder="Default"
            />
            <ToggleButtonGroup
              theme={ToggleButtonGroupTheme.defaultButtons}
              buttons={[
                {
                  title: "Heatmap",
                  value: EngagementSurveyReportViewType.HEATMAP,
                  active: viewType === EngagementSurveyReportViewType.HEATMAP,
                },
                {
                  title: "Raw Data",
                  value: EngagementSurveyReportViewType.RAW_DATA,
                  active: viewType === EngagementSurveyReportViewType.RAW_DATA,
                },
              ]}
              onClick={(opt) => setViewType(assertNonNull(opt.value))}
            />
          </div>

          <div className="flex items-center gap-3">
            {!isLoadingProgram &&
              !isAdmin &&
              !complianceProgram?.hasAccessToAppliesToUsers && (
                <Tooltip
                  text={`You do not have the access privileges to view all the responses within the ${label(
                    "organization",
                  )}.`}
                >
                  <div className="text-sm bg-amber-100 py-2 px-3 rounded-md flex items-center gap-2">
                    <TopicflowIcon
                      type="warning"
                      className="text-amber-700 h-5 w-5"
                    />
                    <div>Not all responses are shown</div>
                  </div>
                </Tooltip>
              )}
            <Button
              disabled={
                isLoadingProgram ||
                isSavingReportingView ||
                isDeletingReportingView
              }
              className="flex items-center"
              theme={buttonTheme.iconGray}
              onClick={() => setShowViewOptionsSidebar(true)}
              aria-label="View Options"
            >
              <TopicflowIcon type="adjustments" className="h-4 w-4" />
            </Button>
            {viewOptions.isDirty && (
              <>
                <Button
                  size={ButtonSize.large}
                  theme={buttonTheme.text}
                  disabled={isSavingReportingView || isDeletingReportingView}
                  text="Reset"
                  onClick={handleResetViewOptionsToBaseline}
                />
                <Button
                  disabled={isSavingReportingView || isDeletingReportingView}
                  size={ButtonSize.large}
                  theme={buttonTheme.primary}
                  className="flex items-center gap-1"
                  onClick={handleSaveExistingView}
                  text="Save View"
                />
              </>
            )}
            <Dropdown
              align="end"
              disabled={reportDropdownActions.length === 0}
              options={reportDropdownActions}
            />
          </div>
        </div>
      </div>

      {viewType === EngagementSurveyReportViewType.HEATMAP && (
        <div
          className={classNames(
            "pb-12",
            isInReporting && "absolute inset-0 overflow-y-scroll px-8 pt-36",
          )}
        >
          {isLoadingProgram && (
            <div className="flex-1 flex justify-center">
              <Loading />
            </div>
          )}
          {!isLoadingProgram &&
            (orgRollup?.responderCount ?? 0) === 0 &&
            (orgRollup?.totalResponders ?? 0) === 0 && (
              <Layout.MainSubSectionEmpty>
                <div className="flex items-center justify-center gap-2.5 text-gray-400">
                  <TopicflowIcon type="info" className="h-4 w-4" />
                  <div className="text-sm">
                    Not enough responses to show results
                  </div>
                </div>
              </Layout.MainSubSectionEmpty>
            )}
          {(orgRollup?.totalResponders ?? 0) > 0 &&
            (rangeQuestions.length > 0 || npsQuestions.length > 0) &&
            !isLoadingProgram && (
              <EngagementSurveyReportTable
                responders={responders}
                npsQuestionRows={npsQuestions}
                rangeQuestionRows={rangeQuestions}
              />
            )}
        </div>
      )}

      {viewType === EngagementSurveyReportViewType.RAW_DATA && (
        <div
          className={classNames(
            "pb-12",
            isInReporting && "absolute inset-0 overflow-y-scroll px-8 pt-36",
          )}
        >
          {isLoadingAssessmentDelivery && (
            <div className="flex-1 flex justify-center">
              <Loading />
            </div>
          )}
          {!isLoadingAssessmentDelivery && (
            <EngagementSurveyDeliveryAllQuestions
              assessmentDeliveryData={assessmentDeliveryData}
              viewOptions={viewOptions}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EngagementSurveyReport;
